import React from 'react'
import { connect as connectRedux } from 'react-redux'
import { Map } from 'immutable'
import { useMutation } from '@apollo/client'
import { getBoundActions } from '../../../actions/index'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { URL_ACCOUNT_CREATION } from '../../../services/url/constants'
import { COURSE_START, COURSE_RESET } from '../../../services/detail/query'
import {
  COURSE_STATE_IN_PROGRESS,
  COURSE_STATE_COMPLETE,
  COURSE_STATE_NOT_STARTED,
} from '../../../services/detail/index'
import Button, { TYPES } from '../../Button/index'
import PlayIcon from '../../Icon/icons/Play/index'
import ReplayIcon from '../../Icon/icons/Replay/index'
import DetailPageV3ActionTrailer from '../DetailPageV3ActionButtons/DetailPageV3ActionTrailer'
import DetailPageV3ActionComments from '../DetailPageV3ActionButtons/DetailPageV3ActionComments'
import DetailPageV3ActionCopyLink from '../DetailPageV3ActionButtons/DetailPageV3ActionCopyLink'
import DetailPageV3CourseFeaturedContent from './DetailPageV3CourseFeaturedContent'
import { checkIfCourseWithoutProgress } from '../utils'

/**
 * @param {Object} props - The props
 * @param {Map} props.auth - The auth data
 * @param {Map} props.detailData - The detail data
 * @param {Map} props.featuredContent - The featured content data
 * @param {Map} props.staticText - The static text
 * @param {Map} props.progressData - The course progress data
 * @param {function} props.onCourseStateChange - The function to change the course state
 * @returns {JSX.Element} DetailPageV3CourseJumbotronActions
 */
const DetailPageV3CourseJumbotronActions = (props) => {
  const {
    auth,
    detailData = Map(),
    featuredContent = Map(),
    progressData = Map(),
    ga4EventData,
    staticText,
    onCourseStateChange,
  } = props

  const jwt = auth.get('jwt')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const isAllowed = featuredContent.getIn(['featureMedia', 'contentPolicy', 'accessCheck', 'allowed'])
  const isCountryAllowed = featuredContent.getIn(['featureMedia', 'contentPolicy', 'accessCheck', 'countryAllowed'])
  const titleGroupId = detailData.get('titleGroupId')
  const isCourseWithoutProgress = checkIfCourseWithoutProgress(detailData.get('contentId'))
  const courseState = isCourseWithoutProgress ? COURSE_STATE_NOT_STARTED : progressData.get('state')

  const [courseStart, { loading: courseStartLoading }] = useMutation(COURSE_START, {
    variables: {
      titleGroupId,
    },
    context: {
      jwt,
    },
  })

  const [courseReset, { loading: courseResetLoading }] = useMutation(COURSE_RESET, {
    variables: {
      titleGroupId,
    },
    context: {
      jwt,
    },
  })

  const handleStartButtonClick = async () => {
    await courseStart()
    onCourseStateChange()
  }

  const handleRestartButtonClick = async () => {
    await courseReset()
    onCourseStateChange()
  }

  const handleContinueCourseClick = async () => {
  }

  if (featuredContent.size === 0) {
    return null
  }

  if (!isCountryAllowed) {
    return (
      <div className="detail-page-v3__course-jumbotron-actions">
        <p className="detail-page-v3__course-jumbotron-unavailable">
          {staticText.get('notAvailableInArea')}
        </p>
      </div>
    )
  }

  if (!isLoggedIn || !isAllowed) {
    return (
      <div className="detail-page-v3__course-jumbotron-actions">
        <Button
          className="detail-page-v3__course-start-button"
          type={TYPES.PRIMARY}
          url={URL_ACCOUNT_CREATION}
        >
          {!isLoggedIn ? staticText.get('signUpToWatch') : staticText.get('upgradeToWatch')}
        </Button>
      </div>
    )
  }

  if (courseState === COURSE_STATE_COMPLETE) {
    return (
      <div className="detail-page-v3__course-jumbotron-actions">
        <Button
          className="detail-page-v3__course-start-button"
          type={TYPES.PRIMARY}
          inverted
          onClick={handleRestartButtonClick}
          disabled={courseResetLoading}
        >
          <ReplayIcon size={32} />
          {staticText.get('restartCourse')}
        </Button>
        <div className="detail-page-v3__course-jumbotron-actions-secondary">
          <DetailPageV3ActionComments url={`${detailData.get('path')}#commentsBlock`} />
          <DetailPageV3ActionCopyLink detailData={detailData} />
        </div>
      </div>
    )
  }

  if (courseState === COURSE_STATE_IN_PROGRESS) {
    return (
      <div>
        <DetailPageV3CourseFeaturedContent featuredContent={featuredContent} />
        <div className="detail-page-v3__course-jumbotron-actions">
          <Button
            className="detail-page-v3__course-continue-button"
            type={TYPES.PRIMARY}
            onClick={handleContinueCourseClick}
            url={featuredContent.get('path')}
            query={{ fullplayer: 'feature' }}
          >
            <PlayIcon size={32} />
            {staticText.get('continueCourse')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="detail-page-v3__course-jumbotron-actions">
      {isCourseWithoutProgress
        ? <Button
            className="detail-page-v3__course-start-button"
            type={TYPES.PRIMARY}
            url={`${detailData.get('path')}#courseModules`}
            disabled={courseStartLoading}
          >
            <PlayIcon size={32} />
            {staticText.get('viewVideos')}
          </Button>
        : <Button
            className="detail-page-v3__course-start-button"
            type={TYPES.PRIMARY}
            onClick={handleStartButtonClick}
            url={featuredContent.get('path')}
            query={{ fullplayer: 'feature' }}
            disabled={courseStartLoading}
          >
            <PlayIcon size={32} />
            {staticText.get('startCourse')}
          </Button>
      }
      <div className="detail-page-v3__course-jumbotron-actions-secondary">
        <DetailPageV3ActionTrailer detailData={detailData} ga4EventData={ga4EventData} />
        <DetailPageV3ActionComments url={`${detailData.get('path')}#commentsBlock`} />
      </div>
    </div>
  )
}

export default connectRedux((state) => ({
  auth: state.auth,
  staticText: state.staticText.getIn(['data', 'detailPageV3', 'data'], Map()),
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(DetailPageV3CourseJumbotronActions)
